import { supabase } from "./supabase";

/**
 * Per-judge email notification preferences. v1 has three booleans, all
 * defaulting to true (judges opt out, not in). The deadline_24h and
 * admin_message switches surface in the UI now but are wired to no
 * firing logic yet — that's a follow-up.
 *
 * The preferences row is auto-created by a DB trigger when a
 * `judge_profiles` row is inserted (i.e. on invite acceptance). The
 * read path still falls back to defaults if the row is somehow
 * missing — defensive belt-and-braces.
 */
export type JudgeNotificationPrefs = {
  email_on_assignment: boolean;
  email_on_deadline_24h: boolean;
  email_on_admin_message: boolean;
};

export type JudgeNotificationPrefKey = keyof JudgeNotificationPrefs;

export const JUDGE_NOTIFICATION_PREFS_DEFAULTS: JudgeNotificationPrefs = {
  email_on_assignment: true,
  email_on_deadline_24h: true,
  email_on_admin_message: true,
};

export async function getJudgeNotificationPrefs(): Promise<JudgeNotificationPrefs> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return { ...JUDGE_NOTIFICATION_PREFS_DEFAULTS };
  const { data, error } = await supabase
    .from("judge_notification_preferences")
    .select("email_on_assignment, email_on_deadline_24h, email_on_admin_message")
    .eq("judge_id", userData.user.id)
    .maybeSingle();
  if (error || !data) {
    if (error) console.warn("getJudgeNotificationPrefs: read failed", error);
    return { ...JUDGE_NOTIFICATION_PREFS_DEFAULTS };
  }
  return data as JudgeNotificationPrefs;
}

/**
 * Single-field upsert. Uses upsert (judge_id, [field]) so we don't have to
 * care whether the row exists — covers the edge case where the trigger
 * didn't fire (manual judge_profiles inserts, etc).
 */
export async function updateJudgeNotificationPref(
  key: JudgeNotificationPrefKey,
  value: boolean
): Promise<{ error: Error | null }> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return { error: new Error("Not signed in") };
  const { error } = await supabase
    .from("judge_notification_preferences")
    .upsert(
      {
        judge_id: userData.user.id,
        [key]: value,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "judge_id" }
    );
  return { error: error ? new Error(error.message) : null };
}
